// Fixed JSX syntax error - removed duplicate closing tags
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Chip,
  Tabs,
  Tab,
  Input,
  Textarea,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRefresh,
  faExclamationTriangle,
  faCheckCircle,
  faChevronDown,
  faChevronRight,
  faCopy,
  faEdit,
  faSave,
  faSearch,
  faLink,
  faBell,
  faLightbulb,
  faTrophy,
  faBrain,
  faShield,
} from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import EditableSection from "../../components/StandardsEditor/EditableSection";
import {
  getDataWithFilterUrl,
  getAlldata,
  PublishData,
} from "../../controllers/APIController";
import useGetAllOrganization from "../../hooks/useGetAllOrganization";
import StandardsPoliciesMapping from "./StandardsPoliciesMapping";
import {
  StandardCancelButton,
  StandardCreateButton,
  StandardAddButton,
  StandardPrimaryButton,
  StandardSecondaryButton,
  StandardDeleteButton,
} from "../../components/StandardButtons";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// AI Configuration
const AI_CONFIG = {
  providers: [
    {
      key: "haya",
      label: "HAYA AI",
      icon: faBrain,
      description: "Default AI provider for compliance analysis",
    },
    {
      key: "openai",
      label: "OpenAI GPT-4",
      icon: faBrain,
      description: "Best for complex analysis and creativity",
    },
    {
      key: "claude",
      label: "Claude (Anthropic)",
      icon: faBrain,
      description: "Excellent for compliance and risk assessment",
    },
    {
      key: "azure",
      label: "Azure OpenAI",
      icon: faBrain,
      description: "Enterprise-grade, better compliance",
    },
    {
      key: "local",
      label: "Local LLM (Ollama)",
      icon: faShield,
      description: "Privacy-focused, runs locally",
    },
    {
      key: "perplexity",
      label: "Perplexity AI",
      icon: faBrain,
      description: "Real-time information and research capabilities",
    },
    {
      key: "gemini",
      label: "Google Gemini",
      icon: faBrain,
      description: "Advanced reasoning and multimodal analysis",
    },
    {
      key: "custom",
      label: "Custom Provider",
      icon: faBrain,
      description: "Add your own AI service endpoint",
    },
  ],
  defaultProvider: "haya",
};

// AI Call Function
const callAI = async (prompt, context, selectedProvider) => {
  try {
    // Simulate AI call - replace with actual implementation
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const responses = [
      "Based on my analysis, this standard shows strong compliance with current regulations. I recommend focusing on the environmental metrics section for improvement.",
      "The mapping suggests several areas for enhancement. Consider expanding the social governance metrics to align with industry best practices.",
      "This standard demonstrates good baseline compliance. I suggest implementing additional monitoring for the governance section.",
    ];

    return responses[Math.floor(Math.random() * responses.length)];
  } catch (error) {
    console.error("AI call error:", error);
    throw error;
  }
};

// Recursive component to display hierarchical standard structure
const StandardSection = ({ section, level = 0, sectionIndex }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0); // Auto-expand top level

  // Handle both 'children' and 'sections' arrays for flexibility
  const hasChildren = section.children && section.children.length > 0;
  const hasMetrics = section.metrics && section.metrics.length > 0;
  const hasContent = hasChildren || hasMetrics;

  const getLevelIndent = (level) => {
    return level * 16; // 16px indent per level
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <div
        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
          hasContent ? "hover:bg-slate-50" : ""
        }`}
        onClick={() => hasContent && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-3"
            style={{ marginLeft: `${getLevelIndent(level)}px` }}
          >
            {hasContent && (
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="w-3 h-3 text-slate-500 transition-transform"
              />
            )}
            <div className="flex items-center gap-2">
              <span className="text-slate-800">{section.name}</span>
              {section.code && (
                <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                  {section.code}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasMetrics && (
              <span className="text-xs text-slate-500">
                {section.metrics.length} metrics
              </span>
            )}
            {hasChildren && (
              <span className="text-xs text-slate-500">
                {section.children.length} items
              </span>
            )}
          </div>
        </div>

        {section.description && (
          <p
            className="text-slate-600 mt-2"
            style={{ marginLeft: `${getLevelIndent(level) + 24}px` }}
          >
            {section.description}
          </p>
        )}
      </div>

      {/* Expandable Content */}
      {isExpanded && hasContent && (
        <div className="border-t border-slate-200 p-4 pt-2">
          <div className="space-y-3">
            {/* Display Children (recursive) */}
            {hasChildren &&
              section.children.map((child, childIndex) => (
                <StandardSection
                  key={child.id || `${sectionIndex}-${childIndex}`}
                  section={child}
                  level={level + 1}
                  sectionIndex={`${sectionIndex}-${childIndex}`}
                />
              ))}

            {/* Display Metrics */}
            {hasMetrics && (
              <div className="space-y-2">
                <div className="text-slate-700">Metrics:</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {section.metrics.map((metric, metricIndex) => (
                    <div
                      key={metric.id || metricIndex}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-slate-700">
                          {metric.name || `Metric ${metricIndex + 1}`}
                        </span>
                        {metric.code && (
                          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">
                            {metric.code}
                          </span>
                        )}
                      </div>
                      {metric.description && (
                        <p className="text-slate-500">{metric.description}</p>
                      )}
                      {metric.type && (
                        <p className="text-slate-400 mt-1">
                          Type: {metric.type}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BuiltinStandardsMapping = () => {
  const { _orgId: currOrg } = useParams();
  const { OrgList, isLoading: isLoadingOrgList } = useGetAllOrganization();
  const [builtinStandards, setBuiltinStandards] = useState([]);
  const [customStandards, setCustomStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [standardsCount, setStandardsCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingCustomStandards, setIsLoadingCustomStandards] =
    useState(false);
  const [customStandardsCount, setCustomStandardsCount] = useState(0);
  const [showCopyModalOpen, setShowCopyModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Create Custom Standard state
  const [showCreateCustomModal, setShowCreateCustomModal] = useState(false);
  const [createStandardName, setCreateStandardName] = useState("");
  const [createStandardDescription, setCreateStandardDescription] =
    useState("");
  const [createStandardFramework, setCreateStandardFramework] = useState("");
  const [createStandardCustomFramework, setCreateStandardCustomFramework] =
    useState("");
  const [createStandardVersion, setCreateStandardVersion] = useState("");
  const [createStandardCode, setCreateStandardCode] = useState("");
  const [isCreatingStandard, setIsCreatingStandard] = useState(false);

  // Reset create standard form
  const resetCreateStandardForm = () => {
    setCreateStandardName("");
    setCreateStandardDescription("");
    setCreateStandardFramework("");
    setCreateStandardCustomFramework("");
    setCreateStandardVersion("");
    setCreateStandardCode("");
  };

  // Add Section (Custom Standards) state
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionCode, setNewSectionCode] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");
  const [isAddingSection, setIsAddingSection] = useState(false);

  // AI State Variables
  const [selectedProvider, setSelectedProvider] = useState(
    AI_CONFIG.defaultProvider
  );
  const [isAILoading, setIsAILoading] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [complianceScore, setComplianceScore] = useState(null);
  const [regulatoryUpdates, setRegulatoryUpdates] = useState([]);
  const [aiReportTemplate, setAiReportTemplate] = useState("");
  const [complianceTrends, setComplianceTrends] = useState([]);

  // AI Modal States
  const [showAICreateModal, setShowAICreateModal] = useState(false);
  const [showAIEnhanceModal, setShowAIEnhanceModal] = useState(false);
  const [showAIReportModal, setShowAIReportModal] = useState(false);
  const [showAITrendsModal, setShowAITrendsModal] = useState(false);
  const [showAIComplianceModal, setShowAIComplianceModal] = useState(false);
  const [showAIMappingModal, setShowAIMappingModal] = useState(false);

  // New AI Feature Modal States
  const [showSBTModal, setShowSBTModal] = useState(false);
  const [showGapAssessmentModal, setShowGapAssessmentModal] = useState(false);
  const [showSmartMappingModal, setShowSmartMappingModal] = useState(false);
  const [showRegulatoryMonitorModal, setShowRegulatoryMonitorModal] =
    useState(false);
  const [showMaterialityModal, setShowMaterialityModal] = useState(false);
  const [showReportQualityModal, setShowReportQualityModal] = useState(false);
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);

  // AI Input States
  const [aiCreateInput, setAiCreateInput] = useState("");
  const [aiEnhanceInput, setAiEnhanceInput] = useState("");
  const [aiReportInput, setAiReportInput] = useState("");
  const [aiTrendsInput, setAiTrendsInput] = useState("");
  const [aiComplianceInput, setAiComplianceInput] = useState("");
  const [aiMappingInput, setAiMappingInput] = useState("");

  // AI Processing States
  const [isAICreating, setIsAICreating] = useState(false);
  const [isAIEnhancing, setIsAIEnhancing] = useState(false);
  const [isAIReporting, setIsAIReporting] = useState(false);
  const [isAITrending, setIsAITrending] = useState(false);
  const [isAIComplianceChecking, setIsAIComplianceChecking] = useState(false);
  const [isAIMapping, setIsAIMapping] = useState(false);

  // Copy Standard Modal States
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copyStandardName, setCopyStandardName] = useState("");

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingStandard, setEditingStandard] = useState(null);
  const [originalStandard, setOriginalStandard] = useState(null);

  // Conflict handling state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Add state to track active tab
  const [activeTab, setActiveTab] = useState("standards");

  // AI Feature Selection State
  const [selectedAIFeature, setSelectedAIFeature] = useState(null);
  const clientQuery = useQueryClient();

  // Get user data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const builtinFilterUrl = `filters[Metric_Id][$eqi]=Reporting Standards`;
  const customFilterUrl = `filters[Metric_Id][$eqi]=Custom Standards`;

  const { data: buildResponseData,isLoading } = useQuery({
    queryKey: ["responses", builtinFilterUrl],
    queryFn: () => getDataWithFilterUrl("responses", builtinFilterUrl),
  });

  const { data: allDataResponse } = useQuery({
    queryKey: ["metrics"],
    queryFn: () => getAlldata("metrics"),
  });

  const { data: customResponseData } = useQuery({
    queryKey: ["responses", customFilterUrl],
    queryFn: () => getDataWithFilterUrl("responses", customFilterUrl),
  });

  useEffect(() => {
    if (buildResponseData?.data && buildResponseData.data.length > 0) {
      // Process all reporting standards records
      let standardsArray = [];

      buildResponseData.data.forEach((record, index) => {
        const jsonSchema = record?.attributes?.JsonSchema;

        if (jsonSchema) {
          let standards = [];

          // Handle the new flatData structure
          if (jsonSchema.flatData && jsonSchema.flatData["Json Structure"]) {
            try {
              const detailedStructure = JSON.parse(
                jsonSchema.flatData["Json Structure"]
              );

              if (
                detailedStructure.children &&
                Array.isArray(detailedStructure.children)
              ) {
                // This is a detailed structure with children, add it as one standard
                standards = [detailedStructure];
              } else if (detailedStructure.id && detailedStructure.name) {
                // This is a single standard object
                standards = [detailedStructure];
              }
            } catch (parseError) {
              console.error(
                "Error parsing Json Structure from flatData:",
                parseError
              );
              console.error(
                "Raw Json Structure value:",
                jsonSchema.flatData["Json Structure"]
              );
            }
          }
          // Handle legacy structures as fallback
          else if (
            jsonSchema.standards &&
            Array.isArray(jsonSchema.standards)
          ) {
            // Basic standards array - add each standard individually
            standards = jsonSchema.standards;
          } else if (jsonSchema["Json Structure"]) {
            // Try to parse the Json Structure string
            try {
              const detailedStructure = JSON.parse(
                jsonSchema["Json Structure"]
              );

              if (
                detailedStructure.children &&
                Array.isArray(detailedStructure.children)
              ) {
                // This is a detailed structure with children, add it as one standard
                standards = [detailedStructure];
              } else if (detailedStructure.id && detailedStructure.name) {
                // This is a single standard object
                standards = [detailedStructure];
              }
            } catch (parseError) {
              console.error("Error parsing Json Structure:", parseError);
              console.error(
                "Raw Json Structure value:",
                jsonSchema["Json Structure"]
              );
              // Fallback: if parsing fails, check if jsonSchema itself is a standard
              if (jsonSchema.id && jsonSchema.name) {
                standards = [jsonSchema];
              }
            }
          } else if (
            jsonSchema.children &&
            Array.isArray(jsonSchema.children)
          ) {
            // Direct children structure
            standards = [jsonSchema];
          } else if (jsonSchema.id && jsonSchema.name) {
            // Single standard object
            standards = [jsonSchema];
          }

          // Add to standards array if we found valid data
          if (standards.length > 0) {
            standardsArray.push(...standards);
          }
        }
      });

      setBuiltinStandards(standardsArray);
      setStandardsCount(standardsArray.length);
    } else {
      console.log("No builtin standards found");
      setBuiltinStandards([]);
      setStandardsCount(0);
    }
    // loadAvailableMetrics();
    if (allDataResponse?.data) {
      setAvailableMetrics(allDataResponse.data);
    }
  }, [buildResponseData, allDataResponse]);

  // Consolidated useEffect for custom standards - prevents race conditions
  useEffect(() => {
    console.log("useEffect triggered with:", {
      currOrg,
      OrgListLength: OrgList?.length,
      isLoadingOrgList,
    });

    // Only load custom standards when organization data is ready
    if (currOrg && OrgList && OrgList.length > 0 && !isLoadingOrgList) {
      console.log("Organization data ready, loading custom standards");
      loadCustomStandards();
    } else {
      console.log("Organization data not ready yet:", {
        hasCurrOrg: !!currOrg,
        hasOrgList: !!OrgList,
        orgListLength: OrgList?.length || 0,
        isLoadingOrgList,
      });
    }
  }, [currOrg, OrgList, isLoadingOrgList, customResponseData]); // Remove activeTab dependency to prevent unnecessary reloads

  // Load custom standards from responses table - improved error handling
  const loadCustomStandards = async () => {
    if (!currOrg || !OrgList || OrgList.length === 0) {
      console.log(
        "No organization data available, skipping custom standards load"
      );
      return;
    }

    if (isLoadingCustomStandards) {
      return;
    }

    try {
      setIsLoadingCustomStandards(true);

      // For now, let's try to load custom standards without organization filter
      // since the organization ID mapping might be different

      // let customResponse;
      try {
        // Remove aggressive timeout - let the browser handle timeouts naturally
        // customResponse = await getDataWithFilterUrl("responses", customFilterUrl);
      } catch (apiError) {
        console.error("API call failed:", apiError);
        // Don't throw the error, just log it and continue with empty results

        customResponseData = { data: [] };
      }

      if (customResponseData?.data && customResponseData.data.length > 0) {
        const standards = customResponseData.data
          .map((record, index) => {
            const jsonSchema = record?.attributes?.JsonSchema;

            // Check all possible data structures
            if (
              jsonSchema &&
              jsonSchema.flatData &&
              jsonSchema.flatData["Json Structure"]
            ) {
              console.log("Found flatData structure with Json Structure");
              try {
                // Parse the JSON structure from the flatData
                const parsedStructure = JSON.parse(
                  jsonSchema.flatData["Json Structure"]
                );
                console.log(
                  "Successfully parsed Json Structure:",
                  parsedStructure
                );
                // Add metadata from the flatData
                return {
                  ...parsedStructure,
                  name: jsonSchema.flatData["Standard Name"],
                  isCopied: true,
                  originalStandard: parsedStructure.name,
                  copiedAt: new Date().toISOString(),
                  version: "1.0",
                };
              } catch (error) {
                console.error("Error parsing custom standard JSON:", error);
                return null;
              }
            } else if (jsonSchema && jsonSchema.standards) {
              return jsonSchema.standards.map((standard) => ({
                ...standard,
                isCopied: true,
                copiedAt: new Date().toISOString(),
                version: "1.0",
              }));
            } else if (jsonSchema && jsonSchema["Json Structure"]) {
              console.log("Found direct Json Structure");
              try {
                const parsedStructure = JSON.parse(
                  jsonSchema["Json Structure"]
                );
                return {
                  ...parsedStructure,
                  isCopied: true,
                  copiedAt: new Date().toISOString(),
                  version: "1.0",
                };
              } catch (error) {
                console.error("Error parsing direct Json Structure:", error);
                return null;
              }
            } else {
            }
            return null;
          })
          .filter(Boolean);

        // Flatten the standards array in case we have nested arrays from legacy structure
        const flattenedStandards = standards.flat();

        // Fix any duplicate IDs in the loaded standards
        const fixedStandards = fixDuplicateIds(flattenedStandards);

        setCustomStandards(fixedStandards);
      } else {
        setCustomStandards([]);
        console.log("No custom standards found");
      }
    } catch (error) {
      console.error("Error loading custom standards:", error);
      toast.error("Failed to load custom standards");
    } finally {
      setIsLoadingCustomStandards(false);
    }
  };

  const handleRefreshStandards = async () => {
    try {
      setIsRefreshing(true);
      clientQuery.invalidateQueries({
        queryKey: ["responses", builtinFilterUrl],
      });
      // await loadStandards(); // Reload builtin standards
      if (currOrg && OrgList && OrgList.length > 0) {
        // await loadCustomStandards(); // Also reload custom standards
        clientQuery.invalidateQueries({
          queryKey: ["responses", customFilterUrl],
        });
      }
      toast.success("Standards data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing standards:", error);
      toast.error("Failed to refresh standards. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // AI Handler Functions
  const handleSmartMapping = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Analyze the selected standard and suggest optimal metric mappings for compliance reporting. Standard: ${
        selectedStandard?.name || "None selected"
      }`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };

      const response = await callAI(prompt, context, selectedProvider);
      setAiResponse(response);
      toast.success("Smart mapping analysis completed!");
    } catch (error) {
      console.error("Smart mapping error:", error);
      toast.error("Failed to perform smart mapping. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  const handleGapAnalysis = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Perform a gap analysis for the selected standard: ${
        selectedStandard?.name || "None selected"
      }. Identify missing metrics and compliance gaps.`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };

      const response = await callAI(prompt, context, selectedProvider);
      setAiResponse(response);
      toast.success("Gap analysis completed!");
    } catch (error) {
      console.error("Gap analysis error:", error);
      toast.error("Failed to perform gap analysis. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  const handleComplianceCheck = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Check compliance status for the selected standard: ${
        selectedStandard?.name || "None selected"
      }. Provide compliance score and recommendations.`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };

      const response = await callAI(prompt, context, selectedProvider);
      setAiResponse(response);

      // Simulate compliance score
      const score = Math.floor(Math.random() * 40) + 60; // 60-100
      setComplianceScore(score);

      toast.success("Compliance check completed!");
    } catch (error) {
      console.error("Compliance check error:", error);
      toast.error("Failed to perform compliance check. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  const handleRegulatoryUpdates = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Get latest regulatory updates for the selected standard: ${
        selectedStandard?.name || "None selected"
      }.`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };
      const response = await callAI(prompt, context, selectedProvider);
      setAiResponse(response);

      // Simulate regulatory updates
      const updates = [
        {
          id: 1,
          title: "Updated GHG Protocol",
          description: "New requirements for Scope 3 emissions",
          date: "2024-01-15",
          priority: "High",
        },
        {
          id: 2,
          title: "ESRS Implementation",
          description: "European Sustainability Reporting Standards",
          date: "2024-01-10",
          priority: "Medium",
        },
      ];
      setRegulatoryUpdates(updates);

      toast.success("Regulatory updates retrieved!");
    } catch (error) {
      console.error("Regulatory updates error:", error);
      toast.error("Failed to retrieve regulatory updates. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Generate a compliance report template for the selected standard: ${
        selectedStandard?.name || "None selected"
      }.`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };

      const response = await callAI(prompt, context, selectedProvider);
      setAiReportTemplate(response);
      toast.success("Report template generated!");
    } catch (error) {
      console.error("Report generation error:", error);
      toast.error("Failed to generate report template. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  const handleTrendAnalysis = async () => {
    try {
      setIsAILoading(true);
      const prompt = `Analyze trends for the selected standard: ${
        selectedStandard?.name || "None selected"
      }.`;
      const context = {
        standards: builtinStandards,
        selectedStandard,
        availableMetrics,
      };

      const response = await callAI(prompt, context, selectedProvider);
      setAiResponse(response);

      // Simulate trend data
      const trends = [
        { month: "Jan", compliance: 75, trend: "up" },
        { month: "Feb", compliance: 78, trend: "up" },
        { month: "Mar", compliance: 82, trend: "up" },
      ];
      setComplianceTrends(trends);

      toast.success("Trend analysis completed!");
    } catch (error) {
      console.error("Trend analysis error:", error);
      toast.error("Failed to perform trend analysis. Please try again.");
    } finally {
      setIsAILoading(false);
    }
  };

  // Generate unique ID for copied standards
  const generateUniqueId = (baseId) => {
    if (!baseId) return `custom_${Date.now()}`;

    // Find existing custom standards with similar base IDs
    const existingIds = customStandards
      .map((standard) => standard.id)
      .filter((id) => id && id.startsWith(baseId));

    if (existingIds.length === 0) {
      return `${baseId}_1`;
    }

    // Extract numbers from existing IDs and find the next one
    const numbers = existingIds
      .map((id) => {
        const match = id.match(new RegExp(`^${baseId}_(\\d+)$`));
        return match ? parseInt(match[1]) : 0;
      })
      .filter((num) => num > 0);

    const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    return `${baseId}_${nextNumber}`;
  };

  // Fix duplicate IDs in existing custom standards
  const fixDuplicateIds = (standards) => {
    const idCounts = {};
    const fixedStandards = standards.map((standard) => {
      if (!standard.id) {
        standard.id = `custom_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        return standard;
      }

      if (idCounts[standard.id]) {
        idCounts[standard.id]++;
        const baseId = standard.id.replace(/_\d+$/, "");
        standard.id = `${baseId}_${idCounts[standard.id]}`;
      } else {
        idCounts[standard.id] = 1;
      }

      return standard;
    });

    return fixedStandards;
  };

  // Handle Copy Standard functionality
  const handleCopyStandard = async () => {
    if (!selectedStandard) {
      toast.error("No standard selected for copying");
      return;
    }

    if (!copyStandardName.trim()) {
      toast.error("Please provide a name for the copied standard");
      return;
    }

    try {
      setIsCopying(true);

      // Get user and organization data
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      const organisation = JSON.parse(sessionStorage.getItem("organisation"));
      const orgId =
        organisation?.id ||
        userData?.user?.LastOrgId ||
        userData?.user?.organisation;
      const userId = userData?.user?.id;

      if (!userData || !orgId) {
        throw new Error("User session or organization not found");
      }

      // Generate unique ID for the copied standard
      const baseId = selectedStandard.id || "custom";
      const uniqueId = generateUniqueId(baseId);

      // Create a copy of the standard with the new ID
      const copiedStandardData = {
        ...selectedStandard,
        id: uniqueId,
        name: copyStandardName.trim(),
        isCopied: true,
        originalStandard: selectedStandard.name,
        copiedAt: new Date().toISOString(),
        version: "1.0",
      };

      // Create the copied standard with the exact same structure as the original
      const copiedStandard = {
        flatData: {
          "Standard Name": copyStandardName.trim(),
          "Json Structure": JSON.stringify(copiedStandardData),
        },
        groupedData: {
          _ungroupedFields: {
            "Standard Name": copyStandardName.trim(),
            "Json Structure": JSON.stringify(copiedStandardData),
          },
        },
      };

      // Prepare data for database storage
      const submissionData = {
        Metric_Id: "Custom Standards",
        Status: "Active",
        JsonSchema: copiedStandard,
        Organization: orgId,
        users_permissions_user: userId,
      };

      const result = await PublishData({ data: submissionData }, "responses");

      if (result?.data) {
        toast.success(
          `"${copyStandardName}" copied successfully! You can now view it in the Custom Standards tab.`
        );
        setIsCopyModalOpen(false);
        setCopyStandardName("");

        // Refresh the custom standards specifically
        // await loadCustomStandards();
        clientQuery.invalidateQueries({
          queryKey: ["responses", customFilterUrl],
        });
      } else {
        throw new Error("Failed to save copied standard");
      }
    } catch (error) {
      console.error("Error copying standard:", error);
      toast.error(`Failed to copy standard: ${error.message}`);
    } finally {
      setIsCopying(false);
    }
  };

  // ===== EDITING FUNCTIONS =====

  // Start editing a custom standard
  const handleStartEditing = (standard) => {
    setEditingStandard(JSON.parse(JSON.stringify(standard))); // Deep copy
    setOriginalStandard(JSON.parse(JSON.stringify(standard))); // Keep original for reset
    setIsEditing(true);
    setHasUnsavedChanges(false);
    setSelectedStandard(standard);
  };

  // Stop editing
  const handleStopEditing = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to discard them?"
        )
      ) {
        setIsEditing(false);
        setEditingStandard(null);
        setOriginalStandard(null);
        setHasUnsavedChanges(false);
        setSelectedStandard(null);
      }
    } else {
      setIsEditing(false);
      setEditingStandard(null);
      setOriginalStandard(null);
      setHasUnsavedChanges(false);
      setSelectedStandard(null);
    }
  };

  // Update a section in the editing standard
  const handleSectionUpdate = (sectionId, updatedSection) => {
    if (!editingStandard) return;

    const updateSectionInTree = (sections, targetId, updates) => {
      return sections.map((section) => {
        if (section.id === targetId) {
          return { ...section, ...updates };
        }
        if (section.children && section.children.length > 0) {
          return {
            ...section,
            children: updateSectionInTree(section.children, targetId, updates),
          };
        }
        return section;
      });
    };

    const updatedChildren = updateSectionInTree(
      editingStandard.children,
      sectionId,
      updatedSection
    );
    const newEditingStandard = {
      ...editingStandard,
      children: updatedChildren,
    };

    setEditingStandard(newEditingStandard);
    setSelectedStandard(newEditingStandard);
    setHasUnsavedChanges(true);
  };

  // Add a new child section
  const handleAddChild = (parentId, newChild) => {
    if (!editingStandard) return;

    const addChildToSection = (sections, targetId, child) => {
      return sections.map((section) => {
        if (section.id === targetId) {
          return {
            ...section,
            children: [...(section.children || []), child],
          };
        }
        if (section.children && section.children.length > 0) {
          return {
            ...section,
            children: addChildToSection(section.children, targetId, child),
          };
        }
        return section;
      });
    };

    const updatedChildren = addChildToSection(
      editingStandard.children,
      parentId,
      newChild
    );
    const newEditingStandard = {
      ...editingStandard,
      children: updatedChildren,
    };

    setEditingStandard(newEditingStandard);
    setSelectedStandard(newEditingStandard);
    setHasUnsavedChanges(true);
  };

  // Delete a section
  const handleDeleteSection = (sectionId) => {
    if (!editingStandard) return;

    const deleteSectionFromTree = (sections, targetId) => {
      return sections.filter((section) => {
        if (section.id === targetId) {
          return false; // Remove this section
        }
        if (section.children && section.children.length > 0) {
          section.children = deleteSectionFromTree(section.children, targetId);
        }
        return true;
      });
    };

    const updatedChildren = deleteSectionFromTree(
      editingStandard.children,
      sectionId
    );
    const newEditingStandard = {
      ...editingStandard,
      children: updatedChildren,
    };

    setEditingStandard(newEditingStandard);
    setSelectedStandard(newEditingStandard);
    setHasUnsavedChanges(true);
  };

  // Reorder sections (placeholder for drag & drop)
  const handleReorder = (fromIndex, toIndex, parentId) => {
    if (!editingStandard) return;

    // This will be implemented with drag & drop
    console.log(
      `Reordering from ${fromIndex} to ${toIndex} in parent ${parentId}`
    );
  };

  // Save changes to database
  const handleSaveChanges = async () => {
    if (!editingStandard || !currOrg || !OrgList || OrgList.length === 0)
      return;

    try {
      setIsSaving(true);

      // Prepare the updated structure for storage
      const updatedStructure = {
        flatData: {
          "Standard Name": editingStandard.name,
          "Json Structure": JSON.stringify(editingStandard),
        },
        groupedData: {
          _ungroupedFields: {
            "Standard Name": editingStandard.name,
            "Json Structure": JSON.stringify(editingStandard),
          },
        },
      };

      // Update the database
      const updateData = {
        Metric_Id: "Custom Standards",
        Status: "Active",
        JsonSchema: updatedStructure,
        Organization: currOrg,
        updatedAt: new Date().toISOString(),
      };

      const result = await PublishData({ data: updateData }, "responses");

      if (result?.data) {
        // Update local state
        const updatedCustomStandards = customStandards.map((standard) =>
          standard.id === editingStandard.id ? editingStandard : standard
        );
        setCustomStandards(updatedCustomStandards);

        // Update selected standard
        setSelectedStandard(editingStandard);

        // Reset editing state
        setIsEditing(false);
        setEditingStandard(null);
        setOriginalStandard(null);
        setHasUnsavedChanges(false);

        toast.success("Changes saved successfully!");
      } else {
        throw new Error("Failed to save changes");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error(`Failed to save changes: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset changes to last saved version
  const handleResetChanges = () => {
    if (originalStandard) {
      setEditingStandard(JSON.parse(JSON.stringify(originalStandard)));
      setSelectedStandard(JSON.parse(JSON.stringify(originalStandard)));
      setHasUnsavedChanges(false);
      toast.info("Changes reset to last saved version");
    }
  };

  // AI Feature Helper Functions
  const getAIFeatureTitle = (featureKey) => {
    const titles = {
      sbt: "SBT AI Assistant",
      "gap-assessment": "Gap Assessment AI",
      "smart-mapping": "Smart Mapping AI",
      "regulatory-monitor": "Regulatory Monitor AI",
      materiality: "ESG Materiality AI",
      "report-quality": "Report Quality AI",
      benchmark: "Benchmark AI",
      "smart-mapping-legacy": "Smart Mapping",
      "compliance-check": "Compliance Check",
      "trend-analysis": "Trend Analysis",
      "report-generation": "Report Generation",
      "gap-analysis-legacy": "Gap Analysis",
      "ai-creation": "AI Creation",
    };
    return titles[featureKey] || "AI Feature";
  };

  const getAIFeatureDescription = (featureKey) => {
    const descriptions = {
      sbt: "AI-powered Science-Based Targets validation and optimization",
      "gap-assessment":
        "AI-powered compliance gap analysis and risk assessment",
      "smart-mapping":
        "AI-powered metric mapping and cross-framework alignment",
      "regulatory-monitor": "AI-powered regulatory update monitoring",
      materiality: "AI-powered ESG materiality assessment and analysis",
      "report-quality": "AI-powered report quality validation and improvement",
      benchmark: "AI-powered industry benchmarking and peer comparison",
      "smart-mapping-legacy": "AI-powered metric mapping suggestions",
      "compliance-check": "Automated compliance assessment",
      "trend-analysis": "Performance trends and insights",
      "report-generation": "AI-generated compliance reports",
      "gap-analysis-legacy": "Identify compliance gaps",
      "ai-creation": "Create standards with AI assistance",
    };
    return descriptions[featureKey] || "AI-powered feature";
  };

  const openAIFeatureModal = (featureKey) => {
    switch (featureKey) {
      case "sbt":
        setShowSBTModal(true);
        break;
      case "gap-assessment":
        setShowGapAssessmentModal(true);
        break;
      case "smart-mapping":
        setShowSmartMappingModal(true);
        break;
      case "regulatory-monitor":
        setShowRegulatoryMonitorModal(true);
        break;
      case "materiality":
        setShowMaterialityModal(true);
        break;
      case "report-quality":
        setShowReportQualityModal(true);
        break;
      case "benchmark":
        setShowBenchmarkModal(true);
        break;
      case "smart-mapping-legacy":
        setShowAIMappingModal(true);
        break;
      case "compliance-check":
        setShowAIComplianceModal(true);
        break;
      case "trend-analysis":
        setShowAITrendsModal(true);
        break;
      case "report-generation":
        setShowAIReportModal(true);
        break;
      case "gap-analysis-legacy":
        setShowAIEnhanceModal(true);
        break;
      case "ai-creation":
        setShowAICreateModal(true);
        break;
      default:
        break;
    }
  };

  // Create a stable section id from a name
  function generateSectionId(name) {
    const base = (name || "section")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
    const suffix = Math.random().toString(36).slice(2, 8);
    return `${base || "section"}-${suffix}`;
  }

  // Add a new root section to the selected custom standard
  async function handleAddRootSection() {
    if (!selectedStandard) {
      toast.error("Select a custom standard first");
      return;
    }
    if (!newSectionName.trim()) {
      toast.error("Please provide a section name");
      return;
    }
    try {
      setIsAddingSection(true);
      const newSection = {
        id: generateSectionId(newSectionName),
        name: newSectionName.trim(),
        code: newSectionCode.trim() || undefined,
        description: newSectionDescription.trim() || undefined,
        children: [],
      };

      // If not already editing, start editing this standard
      if (!isEditing) {
        handleStartEditing(selectedStandard);
      }

      // Append to root children
      const updatedChildren = [
        ...(editingStandard?.children || selectedStandard.children || []),
        newSection,
      ];
      const updated = {
        ...(editingStandard || selectedStandard),
        children: updatedChildren,
      };
      setEditingStandard(updated);
      setSelectedStandard(updated);
      setHasUnsavedChanges(true);

      // Clear modal fields
      setNewSectionName("");
      setNewSectionCode("");
      setNewSectionDescription("");
      setShowAddSectionModal(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to add section");
    } finally {
      setIsAddingSection(false);
    }
  }

  // Check for conflicts between current changes and last saved version
  const checkForConflicts = () => {
    if (!editingStandard || !originalStandard) return false;

    // Simple conflict detection - compare JSON strings
    const currentJson = JSON.stringify(editingStandard);
    const originalJson = JSON.stringify(originalStandard);

    if (currentJson !== originalJson) {
      setConflictData({
        current: editingStandard,
        original: originalStandard,
        differences: [],
      });
      setShowConflictModal(true);
      return true;
    }

    return false;
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-4 sm:px-6 lg:px-8 py-4 shadow-sm">
        <div className="max-w-full">
          <h1 className="text-xl font-semibold text-neutral-900">
            Standards Overview
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Built-in sustainability standards provide a foundation for
            compliance and reporting. These standards include frameworks like
            GRI, SASB, TCFD, and others that help organizations establish
            consistent sustainability reporting practices and meet regulatory
            requirements.
          </p>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Standards Overview Card - Hidden */}
          {/* <div className="bg-white rounded-lg border border-neutral-200 shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-3">Standards Overview</h2>
            <p className="text-neutral-600 leading-relaxed">
              Built-in sustainability standards provide a foundation for compliance and reporting. 
              These standards include frameworks like GRI, SASB, TCFD, and others that help organizations 
              establish consistent sustainability reporting practices and meet regulatory requirements.
            </p>
          </div> */}

          <Tabs
            aria-label="Standards and AI Features"
            className="w-full"
            classNames={{
              tabList:
                "gap-2 sm:gap-4 lg:gap-6 w-full relative rounded-none p-0 border-b border-neutral-200 overflow-x-auto",
              cursor: "w-full bg-emerald-50",
              tab: "max-w-fit px-2 sm:px-3 lg:px-4 h-12 whitespace-nowrap",
              tabContent: "group-data-[selected=true]:text-emerald-600",
            }}
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
          >
            <Tab
              key="standards"
              title={`Standards Management ${
                standardsCount > 0 ? `(${standardsCount})` : ""
              }`}
            >
              <div className="py-4">
                <div className="flex flex-col lg:flex-row items-stretch min-h-[70vh] gap-4 lg:gap-6">
                  {/* Left Sidebar - Standards List */}
                  <div className="flex w-full lg:w-[25%]">
                    <div className="w-full">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-slate-900">
                          Standards ({standardsCount})
                        </h4>
                        <Button
                          size="sm"
                          onClick={handleRefreshStandards}
                          startContent={
                            <FontAwesomeIcon
                              icon={faRefresh}
                              className="w-3 h-3"
                            />
                          }
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          isLoading={isRefreshing}
                        >
                          Refresh
                        </Button>
                      </div>

                      {/* Search Input */}
                      <div className="mb-4">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search standards..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                          />
                          {searchTerm && (
                            <button
                              onClick={() => setSearchTerm("")}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Filtered Standards Count */}
                      {searchTerm && (
                        <div className="mb-3 text-sm text-slate-600">
                          Showing{" "}
                          {
                            builtinStandards.filter(
                              (standard) =>
                                standard.name
                                  ?.toLowerCase()
                                  .includes(searchTerm.toLowerCase()) ||
                                standard.description
                                  ?.toLowerCase()
                                  .includes(searchTerm.toLowerCase())
                            ).length
                          }{" "}
                          of {standardsCount} standards for "{searchTerm}"
                        </div>
                      )}

                      {isLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                          <p className="text-slate-600 mt-2">
                            Loading standards...
                          </p>
                        </div>
                      ) : builtinStandards.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-slate-600 mb-4">
                            No standards found
                          </p>
                        </div>
                      ) : builtinStandards.filter(
                          (standard) =>
                            !searchTerm ||
                            standard.name
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            standard.description
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase())
                        ).length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-slate-600 mb-4">
                            No standards match your search
                          </p>
                          <p className="text-sm text-slate-500">
                            Try adjusting your search terms
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {builtinStandards
                            .filter(
                              (standard) =>
                                !searchTerm ||
                                standard.name
                                  ?.toLowerCase()
                                  .includes(searchTerm.toLowerCase()) ||
                                standard.description
                                  ?.toLowerCase()
                                  .includes(searchTerm.toLowerCase())
                            )
                            .map((standard, index) => (
                              <div
                                key={standard.id || index}
                                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                  selectedStandard?.id === standard.id
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-slate-200 hover:border-slate-300"
                                }`}
                                onClick={() => setSelectedStandard(standard)}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-medium text-slate-900">
                                    {standard.name || `Standard ${index + 1}`}
                                  </h5>
                                  <div className="flex items-center gap-2">
                                    <Chip
                                      size="sm"
                                      color={
                                        standard.id?.includes("gri")
                                          ? "success"
                                          : "primary"
                                      }
                                      variant="flat"
                                    >
                                      {standard.id?.includes("gri")
                                        ? "GRI"
                                        : "Built-in"}
                                    </Chip>
                                    {standard.code && (
                                      <Chip
                                        size="sm"
                                        variant="flat"
                                        color="default"
                                        className="text-xs"
                                      >
                                        {standard.code}
                                      </Chip>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="light"
                                      onPress={() => {
                                        setSelectedStandard(standard);
                                        setIsCopyModalOpen(true);
                                      }}
                                      startContent={
                                        <FontAwesomeIcon
                                          icon={faCopy}
                                          className="w-3 h-3"
                                        />
                                      }
                                      className="text-slate-600 hover:text-slate-800"
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                </div>
                                {standard.version && (
                                  <p className="text-sm text-slate-600 mb-1">
                                    Version: {standard.version}
                                  </p>
                                )}
                                {standard.description && (
                                  <p className="text-xs text-slate-500 mb-2">
                                    {standard.description}
                                  </p>
                                )}
                                {standard.children &&
                                  standard.children.length > 0 && (
                                    <div className="text-xs text-slate-500">
                                      {standard.children.length} categories
                                    </div>
                                  )}
                                {!standard.children && (
                                  <div className="text-xs text-slate-500">
                                    Basic structure
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Panel - Standard Structure */}
                  <div className="flex-1 w-full lg:w-auto">
                    {selectedStandard ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-xl font-semibold text-slate-900">
                              {selectedStandard.name}
                            </h3>
                            {selectedStandard.version && (
                              <p className="text-slate-600">
                                Version: {selectedStandard.version}
                              </p>
                            )}
                            {selectedStandard.description && (
                              <p className="text-slate-600 mt-1">
                                {selectedStandard.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Standard Structure Display */}
                        <div className="border border-slate-200 rounded-lg p-4 bg-white">
                          <h4 className="font-medium text-slate-900 mb-3">
                            Standard Structure
                          </h4>
                          {selectedStandard && (
                            <StandardSection
                              section={selectedStandard}
                              level={0}
                              sectionIndex={"root"}
                            />
                          )}
                        </div>

                        {/* AI Response Display */}
                        {aiResponse && (
                          <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                            <h6 className="font-medium text-emerald-700 mb-3">
                              AI Analysis Results
                            </h6>
                            <div className="text-sm text-emerald-800 mb-3">
                              {aiResponse}
                            </div>
                            {complianceScore && (
                              <div className="mb-3">
                                <span className="font-medium">
                                  Compliance Score:{" "}
                                </span>
                                <span
                                  className={`font-bold ${
                                    complianceScore >= 80
                                      ? "text-green-600"
                                      : complianceScore >= 60
                                      ? "text-yellow-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {complianceScore}%
                                </span>
                              </div>
                            )}
                            {regulatoryUpdates.length > 0 && (
                              <div className="mb-3">
                                <div className="font-medium mb-2">
                                  Regulatory Updates:
                                </div>
                                <div className="space-y-2">
                                  {regulatoryUpdates.map((update) => (
                                    <div
                                      key={update.id}
                                      className="p-2 bg-white rounded border"
                                    >
                                      <div className="font-medium text-sm">
                                        {update.title}
                                      </div>
                                      <div className="text-xs text-slate-600">
                                        {update.description}
                                      </div>
                                      <div className="text-xs text-slate-500 mt-1">
                                        {update.date} • Priority:{" "}
                                        {update.priority}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {complianceTrends.length > 0 && (
                              <div className="mb-3">
                                <div className="font-medium mb-2">
                                  Compliance Trends:
                                </div>
                                <div className="flex gap-4">
                                  {complianceTrends.map((trend, index) => (
                                    <div key={index} className="text-center">
                                      <div className="font-medium text-sm">
                                        {trend.month}
                                      </div>
                                      <div className="text-lg font-bold text-emerald-600">
                                        {trend.compliance}%
                                      </div>
                                      <div
                                        className={`text-xs ${
                                          trend.trend === "up"
                                            ? "text-green-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        {trend.trend === "up" ? "↗" : "↘"}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {aiReportTemplate && (
                              <div className="mb-3">
                                <div className="font-medium mb-2">
                                  Generated Report Template:
                                </div>
                                <div className="p-3 bg-white rounded border text-sm">
                                  {aiReportTemplate}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                          Select a Standard
                        </h3>
                        <p className="text-slate-600">
                          Choose a standard from the left sidebar to view its
                          structure and details
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tab>

            <Tab key="custom-standards" title="Custom Standards">
              <div className="py-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-slate-900">
                    Custom Standards ({customStandards.length})
                  </h4>
                  <div className="flex items-center gap-2">
                    <StandardCreateButton
                      size="sm"
                      onClick={() => setShowCreateCustomModal(true)}
                    >
                      New Standard
                    </StandardCreateButton>
                    <StandardPrimaryButton
                      size="sm"
                      onClick={() => setSelectedStandard(null)}
                      startContent={
                        <FontAwesomeIcon icon={faRefresh} className="w-3 h-3" />
                      }
                    >
                      Refresh
                    </StandardPrimaryButton>
                  </div>
                </div>

                {/* Search Input for Custom Standards */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search custom standards..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Filtered Custom Standards Count */}
                {searchTerm && (
                  <div className="mb-3 text-sm text-slate-600">
                    Showing{" "}
                    {
                      customStandards.filter(
                        (standard) =>
                          standard.name
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          standard.description
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase())
                      ).length
                    }{" "}
                    of {customStandards.length} custom standards for "
                    {searchTerm}"
                  </div>
                )}

                {isLoadingCustomStandards ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                    <p className="text-slate-600 mt-2">
                      Loading custom standards...
                    </p>
                  </div>
                ) : customStandards.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600 mb-4">
                      No custom standards found
                    </p>
                  </div>
                ) : customStandards.filter(
                    (standard) =>
                      !searchTerm ||
                      standard.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      standard.description
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
                  ).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-600 mb-4">
                      No custom standards match your search
                    </p>
                    <p className="text-sm text-slate-500">
                      Try adjusting your search terms
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row items-stretch min-h-[60vh] gap-4 lg:gap-6">
                    <div className="w-full lg:w-[25%] space-y-3">
                      {customStandards
                        .filter(
                          (standard) =>
                            !searchTerm ||
                            standard.name
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            standard.description
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase())
                        )
                        .map((standard, index) => (
                          <div
                            key={standard.id || index}
                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                              selectedStandard?.id === standard.id
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() => setSelectedStandard(standard)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-slate-900">
                                {standard.name ||
                                  `Custom Standard ${index + 1}`}
                              </h5>
                              <div className="flex items-center gap-2">
                                <Chip size="sm" color="primary" variant="flat">
                                  Custom
                                </Chip>
                                {standard.code && (
                                  <Chip
                                    size="sm"
                                    variant="flat"
                                    color="default"
                                    className="text-xs"
                                  >
                                    {standard.code}
                                  </Chip>
                                )}
                                <Button
                                  size="sm"
                                  variant="light"
                                  onPress={() => handleStartEditing(standard)}
                                  startContent={
                                    <FontAwesomeIcon
                                      icon={faEdit}
                                      className="w-3 h-3"
                                    />
                                  }
                                  className="text-slate-600 hover:text-slate-800"
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="light"
                                  onPress={() => {
                                    setSelectedStandard(standard);
                                    setIsCopyModalOpen(true);
                                  }}
                                  startContent={
                                    <FontAwesomeIcon
                                      icon={faCopy}
                                      className="w-3 h-3"
                                    />
                                  }
                                  className="text-slate-600 hover:text-slate-800"
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                            {standard.version && (
                              <p className="text-sm text-slate-600 mb-1">
                                Version: {standard.version}
                              </p>
                            )}
                            {standard.description && (
                              <p className="text-xs text-slate-500 mb-2">
                                {standard.description}
                              </p>
                            )}
                            {standard.children &&
                              standard.children.length > 0 && (
                                <div className="text-xs text-slate-500">
                                  {standard.children.length} categories
                                </div>
                              )}
                            {!standard.children && (
                              <div className="text-xs text-slate-500">
                                Basic structure
                              </div>
                            )}
                            {standard.isCopied && (
                              <div className="text-xs text-slate-500 mt-1">
                                Copied from: {standard.originalStandard}
                              </div>
                            )}
                            {standard.copiedAt && (
                              <div className="text-xs text-slate-500">
                                Copied at:{" "}
                                {new Date(
                                  standard.copiedAt
                                ).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                    <div className="flex-1 w-full lg:w-auto">
                      {selectedStandard ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xl font-semibold text-slate-900">
                                {selectedStandard.name}
                              </h3>
                              {selectedStandard.description && (
                                <p className="text-slate-600 mt-1">
                                  {selectedStandard.description}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <StandardSecondaryButton
                                size="sm"
                                onClick={() =>
                                  handleStartEditing(selectedStandard)
                                }
                              >
                                Edit
                              </StandardSecondaryButton>
                              <StandardAddButton
                                size="sm"
                                onClick={() => setShowAddSectionModal(true)}
                              >
                                Add Section
                              </StandardAddButton>
                              <StandardSecondaryButton
                                size="sm"
                                onClick={() => {
                                  setSelectedStandard(selectedStandard);
                                  setIsCopyModalOpen(true);
                                }}
                              >
                                Copy
                              </StandardSecondaryButton>
                            </div>
                          </div>
                          <div className="border border-slate-200 rounded-lg p-4 bg-white">
                            <h4 className="font-medium text-slate-900 mb-3">
                              Standard Structure
                            </h4>
                            <StandardSection
                              section={selectedStandard}
                              level={0}
                              sectionIndex={"custom-root"}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <h3 className="text-lg font-medium text-slate-900 mb-2">
                            Select a Custom Standard
                          </h3>
                          <p className="text-slate-600">
                            Choose a custom standard from the left to view/edit
                            its structure
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Copy Standard Modal */}
                <Modal
                  isOpen={isCopyModalOpen}
                  onOpenChange={setIsCopyModalOpen}
                  size="2xl"
                  classNames={{
                    base: "modal-content-scrollable",
                    header: "flex-shrink-0 px-6 py-4",
                    footer: "flex-shrink-0 px-6 py-4",
                  }}
                >
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader className="flex flex-col gap-1">
                          <h2 className="text-lg font-semibold text-slate-900">
                            Copy Standard
                          </h2>
                        </ModalHeader>
                        <ModalBody className="px-6 py-4">
                          <p className="text-slate-600 mb-4">
                            Enter a name for the copied standard:
                          </p>
                          <Input
                            label="Standard Name"
                            value={copyStandardName}
                            onChange={(e) =>
                              setCopyStandardName(e.target.value)
                            }
                            placeholder="e.g., My Custom Standard"
                            variant="bordered"
                            className="mb-4"
                          />
                        </ModalBody>
                        <ModalFooter>
                          <StandardCancelButton onPress={onClose}>
                            Cancel
                          </StandardCancelButton>
                          <StandardPrimaryButton
                            onPress={handleCopyStandard}
                            isLoading={isCopying}
                          >
                            {isCopying ? "Copying..." : "Copy Standard"}
                          </StandardPrimaryButton>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>

                {/* Editing Modal */}
                <Modal
                  isOpen={isEditing}
                  onOpenChange={setIsEditing}
                  size="5xl"
                  scrollBehavior="inside"
                  classNames={{
                    base: "max-h-[90vh] modal-content-scrollable",
                    body: "modal-body-scrollable modal-scrollbar",
                    header: "flex-shrink-0 px-6 py-4",
                    footer: "flex-shrink-0 px-6 py-4",
                  }}
                >
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader className="flex flex-col gap-1">
                          <h2 className="text-xl font-semibold text-slate-900">
                            Editing Standard:{" "}
                            {editingStandard?.name || "New Standard"}
                          </h2>
                          {hasUnsavedChanges && (
                            <span className="text-sm text-red-500 font-medium">
                              {" "}
                              (Unsaved Changes)
                            </span>
                          )}
                        </ModalHeader>
                        <ModalBody>
                          <div className="py-2">
                            <EditableSection
                              standard={editingStandard}
                              onSectionUpdate={handleSectionUpdate}
                              onAddChild={handleAddChild}
                              onDeleteSection={handleDeleteSection}
                              onReorder={handleReorder}
                              availableMetrics={availableMetrics}
                              isLoading={isSaving}
                              onSave={handleSaveChanges}
                              onReset={handleResetChanges}
                              onStopEditing={handleStopEditing}
                              isEditing={isEditing}
                              hasUnsavedChanges={hasUnsavedChanges}
                            />
                          </div>
                        </ModalBody>
                        <ModalFooter>
                          <StandardCancelButton onPress={onClose}>
                            Cancel
                          </StandardCancelButton>
                          <Button
                            color="success"
                            onPress={handleSaveChanges}
                            isLoading={isSaving}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            {isSaving ? "Saving..." : "Save Changes"}
                          </Button>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>

                {/* Conflict Resolution Modal */}
                <Modal
                  isOpen={showConflictModal}
                  onOpenChange={setShowConflictModal}
                  size="4xl"
                  scrollBehavior="inside"
                  classNames={{
                    base: "modal-content-scrollable",
                    body: "modal-body-scrollable modal-scrollbar",
                    header: "flex-shrink-0 px-6 py-4",
                    footer: "flex-shrink-0 px-6 py-4",
                  }}
                >
                  <ModalContent>
                    {(onClose) => (
                      <>
                        <ModalHeader className="flex flex-col gap-1">
                          <h2 className="text-lg font-semibold text-slate-900">
                            Conflict Detected
                          </h2>
                        </ModalHeader>
                        <ModalBody>
                          <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <p className="text-amber-800 font-medium">
                                There is a conflict between your current changes
                                and the last saved version of this standard.
                              </p>
                              <p className="text-amber-700 mt-2">
                                Would you like to:
                              </p>
                              <ul className="list-disc list-inside text-sm text-amber-700 mt-2 space-y-1">
                                <li>
                                  Discard your current changes and revert to the
                                  last saved version?
                                </li>
                                <li>
                                  Keep your current changes and potentially
                                  overwrite the last saved version?
                                </li>
                                <li>
                                  View the differences between the two versions?
                                </li>
                              </ul>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-slate-700 mb-2">
                                Current Changes:
                              </p>
                              <pre className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs overflow-auto max-h-40 modal-scrollbar">
                                {JSON.stringify(editingStandard, null, 2)}
                              </pre>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-slate-700 mb-2">
                                Last Saved Version:
                              </p>
                              <pre className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs overflow-auto max-h-40 modal-scrollbar">
                                {JSON.stringify(originalStandard, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </ModalBody>
                        <ModalFooter>
                          <StandardDeleteButton
                            onPress={() => {
                              setShowConflictModal(false);
                              handleResetChanges();
                            }}
                          >
                            Discard Changes
                          </StandardDeleteButton>
                          <StandardPrimaryButton
                            onPress={() => {
                              setShowConflictModal(false);
                            }}
                          >
                            Keep Changes
                          </StandardPrimaryButton>
                        </ModalFooter>
                      </>
                    )}
                  </ModalContent>
                </Modal>
              </div>
            </Tab>

            <Tab
              key="standards-policies-mapping"
              title="Standards-Policies Mapping"
            >
              <div className="py-4">
                <StandardsPoliciesMapping orgId={currOrg} userId={null} />
              </div>
            </Tab>

            <Tab key="ai-features" title="AI-Powered Features">
              <div className="py-4">
                <div className="flex items-stretch min-h-[70vh] gap-6">
                  {/* Left Sidebar - AI Features List */}
                  <div className="flex w-[25%]">
                    <div className="w-full">
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">
                          Available AI Features
                        </h4>
                      </div>

                      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
                        {/* SBT AI Assistant */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "sbt"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("sbt")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              SBT AI Assistant
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Science-Based Targets validation
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Smart Validation</span>
                          </div>
                        </div>

                        {/* Gap Assessment AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "gap-assessment"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("gap-assessment")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              Gap Assessment AI
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Compliance gap analysis
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Risk Analysis</span>
                          </div>
                        </div>

                        {/* Smart Mapping AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "smart-mapping"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("smart-mapping")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              Smart Mapping AI
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Cross-framework alignment
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Cross-Framework</span>
                          </div>
                        </div>

                        {/* Regulatory Monitor AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "regulatory-monitor"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() =>
                            setSelectedAIFeature("regulatory-monitor")
                          }
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              Regulatory Monitor AI
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Regulatory update monitoring
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Real-time Updates</span>
                          </div>
                        </div>

                        {/* ESG Materiality AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "materiality"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("materiality")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              ESG Materiality AI
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            ESG materiality assessment
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Stakeholder Insights</span>
                          </div>
                        </div>

                        {/* Report Quality AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "report-quality"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("report-quality")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              Report Quality AI
                            </h5>
                            <Chip size="sm" color="warning" variant="flat">
                              Beta
                            </Chip>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Report quality validation
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Quality Validation</span>
                          </div>
                        </div>

                        {/* Benchmark AI */}
                        <div
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedAIFeature === "benchmark"
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => setSelectedAIFeature("benchmark")}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-slate-900 text-sm">
                              Benchmark AI
                            </h5>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">
                            Industry benchmarking
                          </p>
                          <div className="flex items-center gap-2 text-xs text-emerald-600">
                            <span>Peer Analysis</span>
                          </div>
                        </div>

                        {/* Legacy AI Features */}
                        <div className="border-t border-slate-200 pt-3 mt-3">
                          <h6 className="text-xs font-medium text-slate-600 mb-2">
                            Legacy Features
                          </h6>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "smart-mapping-legacy"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() =>
                              setSelectedAIFeature("smart-mapping-legacy")
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                Smart Mapping
                              </span>
                            </div>
                          </div>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "compliance-check"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() =>
                              setSelectedAIFeature("compliance-check")
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                Compliance Check
                              </span>
                            </div>
                          </div>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "trend-analysis"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() =>
                              setSelectedAIFeature("trend-analysis")
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                Trend Analysis
                              </span>
                            </div>
                          </div>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "report-generation"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-300"
                            }`}
                            onClick={() =>
                              setSelectedAIFeature("report-generation")
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                Report Generation
                              </span>
                            </div>
                          </div>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "gap-analysis-legacy"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() =>
                              setSelectedAIFeature("gap-analysis-legacy")
                            }
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                Gap Analysis
                              </span>
                            </div>
                          </div>

                          <div
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAIFeature === "ai-creation"
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                            onClick={() => setSelectedAIFeature("ai-creation")}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-700">
                                AI Creation
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - AI Feature Content */}
                  <div className="flex-1">
                    {selectedAIFeature ? (
                      <div className="space-y-4">
                        {/* Feature Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-xl font-semibold text-slate-900">
                              {getAIFeatureTitle(selectedAIFeature)}
                            </h3>
                            <p className="text-slate-600 mt-1">
                              {getAIFeatureDescription(selectedAIFeature)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {selectedAIFeature !== "benchmark" && (
                              <Chip size="sm" color="warning" variant="flat">
                                Beta
                              </Chip>
                            )}
                            <Button
                              size="sm"
                              color="primary"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() =>
                                openAIFeatureModal(selectedAIFeature)
                              }
                            >
                              Open Feature
                            </Button>
                          </div>
                        </div>

                        {/* Feature Content */}
                        <div className="border border-slate-200 rounded-lg p-6 bg-white">
                          <div className="text-center py-8">
                            <h4 className="text-lg font-medium text-slate-900 mb-2">
                              {getAIFeatureTitle(selectedAIFeature)}
                            </h4>
                            <p className="text-slate-600 mb-4">
                              {getAIFeatureDescription(selectedAIFeature)}
                            </p>
                            <p className="text-sm text-slate-500">
                              Click "Open Feature" to access the full AI-powered
                              functionality.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                          Select an AI Feature
                        </h3>
                        <p className="text-slate-600">
                          Choose an AI feature from the left sidebar to view its
                          details and functionality
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </BreadCrumbsPage>

      {/* AI Modals */}
      <Modal
        isOpen={showAICreateModal}
        onClose={() => setShowAICreateModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Standards Creation</ModalHeader>
          <ModalBody>
            <Textarea
              label="Creation Requirements"
              placeholder="Describe what type of standard you want to create..."
              value={aiCreateInput}
              onChange={(e) => setAiCreateInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowAICreateModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton
              onPress={() => {
                toast.info("AI standards creation coming soon!");
                setShowAICreateModal(false);
              }}
            >
              Create Standard
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showAIEnhanceModal}
        onClose={() => setShowAIEnhanceModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Gap Analysis</ModalHeader>
          <ModalBody>
            <Textarea
              label="Analysis Requirements"
              placeholder="Describe what gaps you want to analyze..."
              value={aiEnhanceInput}
              onChange={(e) => setAiEnhanceInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowAIEnhanceModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton
              onPress={() => {
                handleGapAnalysis();
                setShowAIEnhanceModal(false);
              }}
              isLoading={isAILoading}
            >
              Analyze Gaps
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showAIReportModal}
        onClose={() => setShowAIReportModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Report Generation</ModalHeader>
          <ModalBody>
            <Textarea
              label="Report Requirements"
              placeholder="Describe what type of report you want to generate..."
              value={aiReportInput}
              onChange={(e) => setAiReportInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowAIReportModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton
              onPress={() => {
                handleGenerateReport();
                setShowAIReportModal(false);
              }}
              isLoading={isAILoading}
            >
              Generate Report
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showAITrendsModal}
        onClose={() => setShowAITrendsModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Trend Analysis</ModalHeader>
          <ModalBody>
            <Textarea
              label="Analysis Requirements"
              placeholder="Describe what trends you want to analyze..."
              value={aiTrendsInput}
              onChange={(e) => setAiTrendsInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowAITrendsModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton
              onPress={() => {
                handleTrendAnalysis();
                setShowAITrendsModal(false);
              }}
              isLoading={isAILoading}
            >
              Analyze Trends
            </StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showAIComplianceModal}
        onClose={() => setShowAIComplianceModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Compliance Check</ModalHeader>
          <ModalBody>
            <Textarea
              label="Compliance Requirements"
              placeholder="Describe what compliance aspects you want to check..."
              value={aiComplianceInput}
              onChange={(e) => setAiComplianceInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setShowAIComplianceModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={() => {
                handleComplianceCheck();
                setShowAIComplianceModal(false);
              }}
              isLoading={isAILoading}
            >
              Check Compliance
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showAIMappingModal}
        onClose={() => setShowAIMappingModal(false)}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>AI Auto-Mapping</ModalHeader>
          <ModalBody>
            <Textarea
              label="Mapping Requirements"
              placeholder="Describe what mapping you want to perform..."
              value={aiMappingInput}
              onChange={(e) => setAiMappingInput(e.target.value)}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setShowAIMappingModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={() => {
                handleSmartMapping();
                setShowAIMappingModal(false);
              }}
              isLoading={isAILoading}
            >
              Perform Mapping
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New AI Feature Modals */}

      {/* SBT AI Assistant Modal */}
      <Modal
        isOpen={showSBTModal}
        onClose={() => setShowSBTModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FontAwesomeIcon
                  icon={faShield}
                  className="w-5 h-5 text-emerald-600"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold">SBT AI Assistant</h3>
                <p className="text-sm text-slate-600">
                  Science-Based Targets validation and optimization
                </p>
              </div>
              <Chip
                size="sm"
                color="warning"
                variant="flat"
                className="ml-auto"
              >
                Beta
              </Chip>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Target Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Target Type"
                    placeholder="e.g., GHG reduction, renewable energy"
                    variant="bordered"
                  />
                  <Input
                    label="Target Year"
                    placeholder="e.g., 2030"
                    variant="bordered"
                  />
                  <Input
                    label="Baseline Year"
                    placeholder="e.g., 2020"
                    variant="bordered"
                  />
                  <Input
                    label="Reduction Percentage"
                    placeholder="e.g., 50%"
                    variant="bordered"
                  />
                </div>
                <Textarea
                  label="Target Description"
                  placeholder="Describe your sustainability target..."
                  variant="bordered"
                  className="mt-4"
                  rows={3}
                />
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI analysis will provide validation feedback, confidence
                  scores, and optimization recommendations.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowSBTModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton>Analyze with AI</StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Gap Assessment AI Modal */}
      <Modal
        isOpen={showGapAssessmentModal}
        onClose={() => setShowGapAssessmentModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">Gap Assessment AI</h3>
              <p className="text-sm text-slate-600">
                Compliance gap analysis and risk assessment
              </p>
            </div>
            <Chip size="sm" color="warning" variant="flat" className="ml-auto">
              Beta
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Assessment Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Framework"
                    placeholder="e.g., GRI, SASB, TCFD"
                    variant="bordered"
                  />
                  <Input
                    label="Industry"
                    placeholder="e.g., Manufacturing, Technology"
                    variant="bordered"
                  />
                  <Input
                    label="Organization Size"
                    placeholder="e.g., Small, Medium, Large"
                    variant="bordered"
                  />
                  <Input
                    label="Region"
                    placeholder="e.g., North America, Europe"
                    variant="bordered"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will analyze compliance gaps, assess risks, and provide
                  mitigation strategies.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setShowGapAssessmentModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Assess with AI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Smart Mapping AI Modal */}
      <Modal
        isOpen={showSmartMappingModal}
        onClose={() => setShowSmartMappingModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">Smart Mapping AI</h3>
              <p className="text-sm text-slate-600">
                Metric mapping and cross-framework alignment
              </p>
            </div>
            <Chip size="sm" color="warning" variant="flat" className="ml-auto">
              Beta
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Mapping Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Source Framework"
                    placeholder="e.g., GRI, SASB"
                    variant="bordered"
                  />
                  <Input
                    label="Target Framework"
                    placeholder="e.g., TCFD, CDP"
                    variant="bordered"
                  />
                  <Input
                    label="Industry"
                    placeholder="e.g., Financial Services"
                    variant="bordered"
                  />
                  <Input
                    label="Organization Size"
                    placeholder="e.g., Large Enterprise"
                    variant="bordered"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will generate smart mappings, cross-framework analysis, and
                  confidence scores.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton
              onPress={() => setShowSmartMappingModal(false)}
            >
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton>Map with AI</StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Regulatory Monitor AI Modal */}
      <Modal
        isOpen={showRegulatoryMonitorModal}
        onClose={() => setShowRegulatoryMonitorModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">Regulatory Monitor AI</h3>
              <p className="text-sm text-slate-600">
                Regulatory update monitoring
              </p>
            </div>
            <Chip size="sm" color="warning" variant="flat" className="ml-auto">
              Beta
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Monitoring Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Jurisdictions"
                    placeholder="e.g., EU, US, UK"
                    variant="bordered"
                  />
                  <Input
                    label="Frameworks"
                    placeholder="e.g., CSRD, SEC, TCFD"
                    variant="bordered"
                  />
                  <Input
                    label="Alert Level"
                    placeholder="e.g., High, Medium, Low"
                    variant="bordered"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Monitoring Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will monitor regulatory updates and provide real-time
                  alerts and analysis.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setShowRegulatoryMonitorModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Start Monitoring
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ESG Materiality AI Modal */}
      <Modal
        isOpen={showMaterialityModal}
        onClose={() => setShowMaterialityModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">ESG Materiality AI</h3>
              <p className="text-sm text-slate-600">
                ESG materiality assessment and analysis
              </p>
            </div>
            <Chip size="sm" color="warning" variant="flat" className="ml-auto">
              Beta
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Analysis Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Industry"
                    placeholder="e.g., Technology, Manufacturing"
                    variant="bordered"
                  />
                  <Input
                    label="Organization Size"
                    placeholder="e.g., Large Enterprise"
                    variant="bordered"
                  />
                  <Input
                    label="Region"
                    placeholder="e.g., North America, Europe"
                    variant="bordered"
                  />
                  <Input
                    label="Stakeholder Groups"
                    placeholder="e.g., Investors, Customers, Employees"
                    variant="bordered"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will assess ESG materiality and provide stakeholder
                  insights and recommendations.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton
              onPress={() => setShowMaterialityModal(false)}
            >
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton>Analyze with AI</StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Report Quality AI Modal */}
      <Modal
        isOpen={showReportQualityModal}
        onClose={() => setShowReportQualityModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">Report Quality AI</h3>
              <p className="text-sm text-slate-600">
                Report quality validation and improvement
              </p>
            </div>
            <Chip size="sm" color="warning" variant="flat" className="ml-auto">
              Beta
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Report Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Framework"
                    placeholder="e.g., GRI, SASB, TCFD"
                    variant="bordered"
                  />
                  <Input
                    label="Report Type"
                    placeholder="e.g., Sustainability, Annual"
                    variant="bordered"
                  />
                </div>
                <Textarea
                  label="Report Content"
                  placeholder="Paste your report content here for AI analysis..."
                  variant="bordered"
                  className="mt-4"
                  rows={6}
                />
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will validate report quality and provide improvement
                  suggestions.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setShowReportQualityModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Validate with AI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Benchmark AI Modal */}
      <Modal
        isOpen={showBenchmarkModal}
        onClose={() => setShowBenchmarkModal(false)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">Benchmark AI</h3>
              <p className="text-sm text-slate-600">
                Industry benchmarking and peer comparison
              </p>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  Benchmark Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Industry"
                    placeholder="e.g., Technology, Manufacturing"
                    variant="bordered"
                  />
                  <Input
                    label="Region"
                    placeholder="e.g., North America, Europe"
                    variant="bordered"
                  />
                  <Input
                    label="Organization Size"
                    placeholder="e.g., Large Enterprise"
                    variant="bordered"
                  />
                  <Input
                    label="Metrics"
                    placeholder="e.g., GHG emissions, water usage"
                    variant="bordered"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 mb-2">
                  AI Analysis Results
                </h4>
                <p className="text-sm text-emerald-700">
                  AI will provide peer comparison results and industry insights.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={() => setShowBenchmarkModal(false)}>
              Cancel
            </StandardCancelButton>
            <StandardPrimaryButton>Benchmark with AI</StandardPrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Custom Standard Modal */}
      <Modal
        isOpen={showCreateCustomModal}
        onOpenChange={(open) => {
          if (!open) {
            resetCreateStandardForm();
          }
          setShowCreateCustomModal(open);
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Create Custom Standard
              </ModalHeader>
              <ModalBody>
                <Input
                  label="Standard Name"
                  value={createStandardName}
                  onChange={(e) => setCreateStandardName(e.target.value)}
                  placeholder="e.g., My Custom Standard"
                />
                <Textarea
                  label="Description"
                  value={createStandardDescription}
                  onChange={(e) => setCreateStandardDescription(e.target.value)}
                  placeholder="Optional description"
                />
                <Select
                  label="Framework"
                  placeholder="Select a framework"
                  selectedKeys={
                    createStandardFramework ? [createStandardFramework] : []
                  }
                  onSelectionChange={(keys) =>
                    setCreateStandardFramework(Array.from(keys)[0] || "")
                  }
                  isRequired
                >
                  <SelectItem key="GRI" value="GRI">
                    GRI (Global Reporting Initiative)
                  </SelectItem>
                  <SelectItem key="SASB" value="SASB">
                    SASB (Sustainability Accounting Standards Board)
                  </SelectItem>
                  <SelectItem key="TCFD" value="TCFD">
                    TCFD (Task Force on Climate-related Financial Disclosures)
                  </SelectItem>
                  <SelectItem key="CDP" value="CDP">
                    CDP (Carbon Disclosure Project)
                  </SelectItem>
                  <SelectItem key="BRSR" value="BRSR">
                    BRSR (Business Responsibility and Sustainability Reporting)
                  </SelectItem>
                  <SelectItem key="ISO" value="ISO">
                    ISO (International Organization for Standardization)
                  </SelectItem>
                  <SelectItem key="Custom" value="Custom">
                    Custom Framework
                  </SelectItem>
                </Select>
                {createStandardFramework === "Custom" && (
                  <Input
                    label="Custom Framework Name"
                    placeholder="Enter your custom framework name"
                    value={createStandardCustomFramework}
                    onChange={(e) =>
                      setCreateStandardCustomFramework(e.target.value)
                    }
                    isRequired
                  />
                )}
                <Input
                  label="Version"
                  value={createStandardVersion}
                  onChange={(e) => setCreateStandardVersion(e.target.value)}
                  placeholder="e.g., 1.0"
                />
                <Input
                  label="Code"
                  value={createStandardCode}
                  onChange={(e) => setCreateStandardCode(e.target.value)}
                  placeholder="e.g., G-1"
                />
              </ModalBody>
              <ModalFooter>
                <StandardCancelButton
                  onPress={() => {
                    resetCreateStandardForm();
                    onClose();
                  }}
                >
                  Cancel
                </StandardCancelButton>
                <StandardCreateButton
                  isLoading={isCreatingStandard}
                  onPress={async () => {
                    if (!createStandardName.trim()) {
                      toast.error("Please provide a name");
                      return;
                    }
                    if (!createStandardFramework.trim()) {
                      toast.error("Please select a framework");
                      return;
                    }
                    if (
                      createStandardFramework === "Custom" &&
                      !createStandardCustomFramework.trim()
                    ) {
                      toast.error("Please provide a custom framework name");
                      return;
                    }
                    setIsCreatingStandard(true);
                    try {
                      const frameworkName =
                        createStandardFramework === "Custom"
                          ? createStandardCustomFramework.trim()
                          : createStandardFramework;
                      const newStandard = {
                        id: generateUniqueId("custom_standard"),
                        name: createStandardName.trim(),
                        description: createStandardDescription.trim(),
                        framework: frameworkName,
                        version: createStandardVersion.trim(),
                        code: createStandardCode.trim(),
                        children: [],
                      };
                      const payload = {
                        Metric_Id: "Custom Standards",
                        Status: "Active",
                        JsonSchema: {
                          flatData: {
                            "Standard Name": newStandard.name,
                            Framework: newStandard.framework,
                            Version: newStandard.version,
                            Code: newStandard.code,
                            Description: newStandard.description,
                            "Json Structure": JSON.stringify(newStandard),
                          },
                          groupedData: {
                            _ungroupedFields: {
                              "Standard Name": newStandard.name,
                              Framework: newStandard.framework,
                              Version: newStandard.version,
                              Code: newStandard.code,
                              Description: newStandard.description,
                              "Json Structure": JSON.stringify(newStandard),
                            },
                          },
                        },
                        Organization: currOrg,
                      };
                      const res = await PublishData(
                        { data: payload },
                        "responses"
                      );
                      if (res?.data) {
                        toast.success("Custom standard created successfully");
                        setShowCreateCustomModal(false);
                        resetCreateStandardForm();
                        // await loadCustomStandards();
                        clientQuery.invalidateQueries({
                          queryKey: ["responses", customFilterUrl],
                        });
                      } else {
                        toast.error("Failed to create standard");
                      }
                    } catch (e) {
                      console.error("Error creating standard:", e);
                      toast.error("Failed to create standard");
                    } finally {
                      setIsCreatingStandard(false);
                    }
                  }}
                >
                  Create
                </StandardCreateButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Add Section Modal */}
      <Modal isOpen={showAddSectionModal} onOpenChange={setShowAddSectionModal}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Add Section
              </ModalHeader>
              <ModalBody>
                <Input
                  label="Section Name"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="e.g., Governance"
                />
                <Input
                  label="Code (optional)"
                  value={newSectionCode}
                  onChange={(e) => setNewSectionCode(e.target.value)}
                  placeholder="e.g., G-1"
                />
                <Textarea
                  label="Description (optional)"
                  value={newSectionDescription}
                  onChange={(e) => setNewSectionDescription(e.target.value)}
                  placeholder="Short description"
                />
              </ModalBody>
              <ModalFooter>
                <StandardCancelButton onPress={onClose}>
                  Cancel
                </StandardCancelButton>
                <StandardAddButton
                  isLoading={isAddingSection}
                  onPress={handleAddRootSection}
                >
                  Add
                </StandardAddButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Toaster />
    </div>
  );
};

export default BuiltinStandardsMapping;
